import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { AuthContext } from './AuthContext';
import supabase from '../supabase/supabase';

export const AppContext = createContext();

export const AppProvider = ({ children }) => {
  const { user, isAuthenticated } = useContext(AuthContext);
  const [invitations, setInvitations] = useState([]);
  const [loadingInvitations, setLoadingInvitations] = useState(false);
  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);

  const loadInvitations = useCallback(async () => {
    const currentUser = userRef.current;
    if (!supabase || !currentUser?.id) return;
    setLoadingInvitations(true);
    try {
      const { data, error } = await supabase
        .from('invitations')
        .select('*')
        .or(`sender_id.eq.${currentUser.id},recipient_id.eq.${currentUser.id}`)
        .order('created_at', { ascending: false });

      if (!error && data) {
        const enriched = data.map(inv => ({
          ...inv,
          direction: inv.sender_id === currentUser.id ? 'sent' : 'received'
        }));
        setInvitations(enriched);
      }
    } catch (err) {
      console.error('Error loading invitations:', err);
    } finally {
      setLoadingInvitations(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && user?.id && supabase) {
      loadInvitations();
    } else {
      setInvitations([]);
    }
  }, [isAuthenticated, user?.id, loadInvitations]);

  const addInvitation = useCallback(async (inviteData) => {
    if (!supabase || !user?.id) return null;
    const token = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

    const newInvite = {
      sender_id: user.id,
      recipient_name: inviteData.recipientName || '',
      category: inviteData.category || 'singles',
      occasion: inviteData.occasion || '',
      template_id: inviteData.template_id || '',
      message: inviteData.message || '',
      title: inviteData.title || '',
      event_date: inviteData.eventDate || null,
      event_time: inviteData.eventTime || '',
      event_location: inviteData.eventLocation || '',
      unique_token: token,
      status: 'pending',
      flow_type: inviteData.flowType || inviteData.category || 'singles',
      sender_display_name: inviteData.senderDisplayName || user?.name || '',
      invitation_question: inviteData.invitationQuestion || '',
      music_track: inviteData.musicTrack || '',
      music_title: inviteData.musicTitle || '',
      photo_url: inviteData.photoUrl || '',
    };

    try {
      const { data, error } = await supabase
        .from('invitations')
        .insert(newInvite)
        .select()
        .single();
      if (error) {
        console.error('Error creating invitation:', error);
        return null;
      }
      const enriched = { ...data, direction: 'sent' };
      setInvitations(prev => [enriched, ...prev]);
      return data.unique_token;
    } catch (err) {
      console.error('Error creating invitation:', err);
      return null;
    }
  }, [user?.id, user?.name]);

  const editInvitation = useCallback(async (token, inviteData) => {
    if (!supabase || !user?.id) return { success: false, error: 'Not authenticated' };

    const updates = {
      recipient_name: inviteData.recipientName || '',
      occasion: inviteData.occasion || '',
      template_id: inviteData.template_id || '',
      message: inviteData.message || '',
      event_date: inviteData.eventDate || null,
      event_time: inviteData.eventTime || '',
      event_location: inviteData.eventLocation || '',
      flow_type: inviteData.flowType || 'singles',
      sender_display_name: inviteData.senderDisplayName || user?.name || '',
      invitation_question: inviteData.invitationQuestion || '',
      music_track: inviteData.musicTrack || '',
      music_title: inviteData.musicTitle || '',
      photo_url: inviteData.photoUrl || '',
      updated_at: new Date().toISOString(),
    };

    try {
      const { data, error } = await supabase
        .from('invitations')
        .update(updates)
        .eq('unique_token', token)
        .eq('sender_id', user.id)
        .select()
        .single();
      if (error) return { success: false, error: error.message };
      setInvitations(prev => prev.map(inv =>
        inv.unique_token === token ? { ...data, direction: inv.direction } : inv
      ));
      return { success: true, data };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [user?.id, user?.name]);

  const updateInvitationStatus = useCallback(async (token, status) => {
    if (!supabase) return;
    try {
      const updates = { status };
      if (status === 'opened') updates.opened_at = new Date().toISOString();
      if (['accepted', 'declined', 'proposed', 'confirmed', 'cancelled', 'replied'].includes(status)) {
        updates.responded_at = new Date().toISOString();
      }
      const { data, error } = await supabase
        .from('invitations')
        .update(updates)
        .eq('unique_token', token)
        .select()
        .single();
      if (!error && data) {
        setInvitations(prev => prev.map(inv =>
          inv.unique_token === token ? { ...data, direction: inv.direction } : inv
        ));
      }
    } catch (err) {
      console.error('Error updating invitation:', err);
    }
  }, []);

  const cancelInvitation = useCallback(async (token) => {
    if (!supabase) return { success: false, error: 'No supabase' };
    try {
      const { data, error } = await supabase
        .from('invitations')
        .update({ status: 'cancelled', responded_at: new Date().toISOString() })
        .eq('unique_token', token)
        .select()
        .single();
      if (error) return { success: false, error: error.message };
      setInvitations(prev => prev.map(inv =>
        inv.unique_token === token ? { ...data, direction: inv.direction } : inv
      ));
      return { success: true, data };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const submitReply = useCallback(async (token, response, message) => {
    if (!supabase) return { success: false, error: 'No supabase' };
    try {
      const { data, error } = await supabase
        .from('invitations')
        .update({
          status: 'replied',
          reply_response: response || '',
          reply_message: message || '',
          responded_at: new Date().toISOString()
        })
        .eq('unique_token', token)
        .select()
        .single();
      if (error) return { success: false, error: error.message };
      setInvitations(prev => prev.map(inv =>
        inv.unique_token === token ? { ...data, direction: inv.direction } : inv
      ));
      return { success: true, data };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const getInvitationByToken = useCallback(async (token) => {
    if (!supabase) return null;
    try {
      const { data, error } = await supabase
        .from('invitations')
        .select('*')
        .eq('unique_token', token)
        .single();

      if (error || !data) return null;

      let senderProfile = null;
      if (data.sender_id) {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('name, avatar_url, bio, interests, hobbies, age, location')
          .eq('id', data.sender_id)
          .maybeSingle();
        senderProfile = profile || null;
      }

      return { ...data, sender_profile: senderProfile };
    } catch (err) {
      console.error('Error fetching invitation:', err);
      return null;
    }
  }, []);

  const createConnectionFromInvite = useCallback(async (inviteToken, recipientUserId) => {
    if (!supabase || !inviteToken || !recipientUserId) return null;
    try {
      const { data: invitation, error: inviteError } = await supabase
        .from('invitations')
        .select('*')
        .eq('unique_token', inviteToken)
        .single();
      if (inviteError || !invitation) {
        console.error('Invitation not found:', inviteError);
        return null;
      }
      if (invitation.sender_id === recipientUserId) return null;

      const { data: existing } = await supabase
        .from('connections')
        .select('id')
        .eq('invitation_id', invitation.id)
        .maybeSingle();
      if (existing) return existing;

      const { data: connection, error: connError } = await supabase
        .from('connections')
        .insert({
          invitation_id: invitation.id,
          sender_id: invitation.sender_id,
          recipient_id: recipientUserId,
          status: 'active'
        })
        .select()
        .single();
      if (connError) {
        console.error('Error creating connection:', connError);
        return null;
      }

      await supabase
        .from('invitations')
        .update({ recipient_id: recipientUserId })
        .eq('unique_token', inviteToken);

      await loadInvitations();
      return connection;
    } catch (err) {
      console.error('Error in createConnectionFromInvite:', err);
      return null;
    }
  }, [loadInvitations]);

  const updateInvitationWithChoices = useCallback(async (token, choices) => {
    if (!supabase) return { success: false, error: 'No supabase' };
    try {
      const { data, error } = await supabase
        .from('invitations')
        .update({
          status: 'proposed',
          receiver_date_type: choices.dateType,
          receiver_custom_type: choices.customType || null,
          receiver_event_date: choices.eventDate,
          receiver_event_time: choices.eventTime,
          receiver_message: choices.message || null,
          responded_at: new Date().toISOString()
        })
        .eq('unique_token', token)
        .select()
        .single();
      if (error) return { success: false, error: error.message };
      setInvitations(prev => prev.map(inv =>
        inv.unique_token === token ? { ...data, direction: inv.direction } : inv
      ));
      return { success: true, data };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const confirmInvitationBySender = useCallback(async (invitationId) => {
    if (!supabase) return { success: false, error: 'No supabase' };
    try {
      const { data, error } = await supabase
        .from('invitations')
        .update({ status: 'confirmed', sender_confirmed: true })
        .eq('id', invitationId)
        .select()
        .single();
      if (error) return { success: false, error: error.message };
      setInvitations(prev => prev.map(inv =>
        inv.id === invitationId ? { ...data, direction: inv.direction } : inv
      ));
      return { success: true, data };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const getSenderProfile = useCallback(async (invitationToken) => {
    if (!supabase) return null;
    try {
      const { data: invitation } = await supabase
        .from('invitations')
        .select('sender_id')
        .eq('unique_token', invitationToken)
        .single();
      if (!invitation) return null;
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('name, avatar_url, bio, interests, hobbies, age, location')
        .eq('id', invitation.sender_id)
        .maybeSingle();
      return profile;
    } catch (err) {
      console.error('Error fetching sender profile:', err);
      return null;
    }
  }, []);

  return (
    <AppContext.Provider value={{
      invitations,
      loadingInvitations,
      currentUser: user,
      addInvitation,
      editInvitation,
      updateInvitationStatus,
      getInvitationByToken,
      createConnectionFromInvite,
      updateInvitationWithChoices,
      confirmInvitationBySender,
      getSenderProfile,
      cancelInvitation,
      submitReply,
      loadInvitations
    }}>
      {children}
    </AppContext.Provider>
  );
};